import { useMemo, useState } from 'react';
import {
  P2GBox,
  P2GImage,
  P2GServiceLogo,
  P2GText,
  SvgEnquiryMoreInfo,
  SvgLocker,
  SvgPrinter,
  SvgPrinterNoPrinter,
  SvgShop,
  SvgVan
} from '@parcel2go/core-ui';
import { CustomerServiceTag, QuoteApiModel, QuoteOptionApiModel } from '~/lib/api/smartsend/types';
import { P2GGrid } from '@parcel2go/core-ui';
import { QuoteTagType } from '../utils/QuoteTagType';
import { CurrentSelectionType } from '../utils/CurrentSelectionType';
import { onlyUnique } from '~/lib/utils/array';
import { CollectionImageName, PolyFillQuoteOptions } from './QuoteCardBase';
import { P2GSidePanel } from '~/components/sidepanel';
import { useServiceLogo } from '~/hooks/use-service-logo';
import ServiceInfo, { ServiceTabType } from '../ServiceInfo';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';
import ServiceTags from '../ServiceTags';
import { Tagged } from '../../../Tagged';
import { QuoteOptionButton } from './QuoteOptionButton';
import QuoteCardContent from './QuoteCardContent';
import { AppCard } from '~/components/card/app-card';
import { CourierLogo } from './CourierLogo';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

export interface QuoteCardProps {
  quote: QuoteApiModel;
  currentSelection?: CurrentSelectionType;
  estimatedValue?: number;
  onSave: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  isSelected: (o: QuoteOptionApiModel) => boolean;
  editMode: QuoteTagType;
  serviceTags: string[];
  updateServiceTags: (add: CustomerServiceTag[], remove: CustomerServiceTag[]) => void;
  toggleSpecificTag: (tag: CustomerServiceTag) => void;
  maxQuoteOptions: number;
  isXs: boolean;
}

const Icons = ({
  quote,
  setTab,
  imageName
}: {
  quote: QuoteApiModel;
  setTab: (tab: ServiceTabType) => void;
  imageName: string;
}) => (
  <P2GGrid container gap={2} alignItems="center" alignContent="center" wrap="nowrap" justify="flex-end">
    <P2GGrid item>
      <CdnIconProxy size="sm" palette="primary" onClick={() => setTab('INFO')} src="/theme/icons/EnquiryMoreInfo.svg">
        <SvgEnquiryMoreInfo />
      </CdnIconProxy>
    </P2GGrid>
    <P2GGrid item>
      {quote.Service.PrinterRequired ? (
        <CdnIconProxy size="sm" palette="primary" onClick={() => setTab('PRINTER')} src="/theme/icons/Printer.svg">
          <SvgPrinter />
        </CdnIconProxy>
      ) : (
        <CdnIconProxy
          size="sm"
          palette="primary"
          onClick={() => setTab('PRINTER')}
          src="/theme/icons/PrinterNoPrinter.svg"
        >
          <SvgPrinterNoPrinter />
        </CdnIconProxy>
      )}
    </P2GGrid>
    <P2GGrid item>
      <CdnIconProxy
        size="sm"
        palette="primary"
        onClick={() => setTab('DELIVERYTYPE')}
        src={`/theme/icons/${imageName}.svg`}
      >
        {imageName === 'Van' && <SvgVan />}
        {imageName === 'Locker' && <SvgLocker />}
        {imageName === 'Shop' && <SvgShop />}
      </CdnIconProxy>
    </P2GGrid>
  </P2GGrid>
);

const DesktopView = ({
  estimatedValue,
  options,
  availableExtras,
  isSelected,
  quote,
  onSave,
  setTab,
  imageName
}: {
  estimatedValue: number;
  options: QuoteOptionApiModel[];
  availableExtras: string[];
  imageName: string;
  isSelected: (o: QuoteOptionApiModel) => boolean;
  quote: QuoteApiModel;
  onSave: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  setTab: (tab: ServiceTabType) => void;
}) => {
  const { getSrc } = useServiceLogo();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <P2GGrid container spacing={4}>
      <P2GGrid item sm={12} lg={6}>
        <P2GGrid container gap={2} alignItems="center" alignContent="center" wrap="nowrap" justify="flex-end">
          <P2GGrid item>
            <P2GImage
              style={{ height: '35px', maxWidth: '180px' }}
              src={getSrc(quote.Service)}
              alt={quote.Service?.Name}
            />
          </P2GGrid>
          <P2GGrid item>
            <P2GText variant="subtitle2">{quote.Service.Name}</P2GText>
          </P2GGrid>
          <P2GGrid item flexGrow={1}></P2GGrid>
          <P2GGrid item>
            <Icons quote={quote} setTab={setTab} imageName={imageName} />
          </P2GGrid>
        </P2GGrid>
        <P2GBox pt={1} sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <P2GBox sx={{ flexGrow: 1 }}>
            <QuoteCardContent quote={quote}>
              <ServiceTags
                setTab={setTab}
                service={quote.Service}
                availableExtras={availableExtras}
                closestPickupLocation={quote.ClosestPickupLocation}
              />
            </QuoteCardContent>
          </P2GBox>
          {quote.Service.FirstMileCourierSlug != quote.Service.CourierId && (
            <CourierLogo
              courierId={quote.Service.FirstMileCourierSlug}
              courierName={quote.Service.FirstMileCourierName}
              width={120}
              wrapper={(children) => (
                <P2GBox sx={{ flexShrink: 1 }}>
                  <P2GBox sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <P2GText variant="caption">
                      {t('QUOTE_FIRST_MILE_LABEL', {
                        defaultValue: 'Collected by:',
                        courier: quote.Service.FirstMileCourierName
                      })}
                    </P2GText>
                    <P2GBox sx={{ border: '1px solid #f1f1f1', borderRadius: 1 }}>{children}</P2GBox>
                  </P2GBox>
                </P2GBox>
              )}
            />
          )}
        </P2GBox>
      </P2GGrid>
      <P2GGrid item sm={12} lg={6}>
        <P2GGrid container spacing={2}>
          {options.map((o, i) => (
            <P2GGrid item key={o.Extras.length === 0 ? `${i}` : o.Extras?.map((x) => x.Id).join('')} xs={12}>
              <AppCard
                className={
                  o.CoverPercentage === 100 ? 'smartsend-quote-option full-protection' : 'smartsend-quote-option'
                }
              >
                <P2GBox p={1.5}>
                  <QuoteOptionButton
                    isXs={false}
                    currencyCode={quote.CurrencyCode}
                    estimatedValue={estimatedValue}
                    option={o}
                    onSelect={() => onSave(quote, o)}
                    isSelected={isSelected(o)}
                  />
                </P2GBox>
              </AppCard>
            </P2GGrid>
          ))}
        </P2GGrid>
      </P2GGrid>
    </P2GGrid>
  );
};

const MobileView = ({
  estimatedValue,
  options,
  availableExtras,
  isSelected,
  quote,
  onSave,
  setTab,
  imageName
}: {
  estimatedValue: number;
  options: QuoteOptionApiModel[];
  availableExtras: string[];
  imageName: string;
  isSelected: (o: QuoteOptionApiModel) => boolean;
  quote: QuoteApiModel;
  onSave: (quote: QuoteApiModel, option: QuoteOptionApiModel) => void;
  setTab: (tab: ServiceTabType) => void;
}) => {
  const { getSrc } = useServiceLogo();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  return (
    <P2GGrid container spacing={2} direction="column">
      <P2GGrid item>
        <P2GGrid container spacing={2} alignItems="center" alignContent="center" wrap="nowrap" justify="space-between">
          <P2GGrid item>
            <P2GImage
              style={{ height: '35px', maxWidth: '180px' }}
              src={getSrc(quote.Service)}
              alt={quote.Service?.Name}
            />
          </P2GGrid>
          <P2GGrid item>
            <Icons quote={quote} setTab={setTab} imageName={imageName} />
          </P2GGrid>
        </P2GGrid>
      </P2GGrid>
      <P2GGrid item>
        <P2GText variant="subtitle2">{quote.Service.Name}</P2GText>
      </P2GGrid>
      <P2GGrid item>
        <QuoteCardContent
          quote={quote}
          tags={
            <ServiceTags
              setTab={setTab}
              service={quote.Service}
              availableExtras={availableExtras}
              closestPickupLocation={quote.ClosestPickupLocation}
            />
          }
        />
      </P2GGrid>
      {quote.Service.FirstMileCourierSlug != quote.Service.CourierId && (
        <CourierLogo
          courierId={quote.Service.FirstMileCourierSlug}
          courierName={quote.Service.FirstMileCourierName}
          width={120}
          wrapper={(children) => (
            <P2GGrid item>
              <P2GBox
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                <P2GText variant="caption">
                  {t('QUOTE_FIRST_MILE_LABEL', {
                    defaultValue: 'Collected by:',
                    courier: quote.Service.FirstMileCourierName
                  })}
                </P2GText>
                <P2GBox sx={{ border: '1px solid #f1f1f1', borderRadius: 1 }}>{children}</P2GBox>
              </P2GBox>
            </P2GGrid>
          )}
        />
      )}
      <P2GGrid item>
        <P2GGrid container direction="column" spacing={2}>
          {options.map((o, i) => (
            <P2GGrid item key={o.Extras.length === 0 ? `${i}` : o.Extras?.map((x) => x.Id).join('')}>
              <AppCard
                className={
                  o.CoverPercentage === 100 ? 'smartsend-quote-option full-protection' : 'smartsend-quote-option'
                }
              >
                <P2GBox p={1.5}>
                  <QuoteOptionButton
                    isXs={true}
                    currencyCode={quote.CurrencyCode}
                    estimatedValue={estimatedValue}
                    option={o}
                    onSelect={() => onSave(quote, o)}
                    isSelected={isSelected(o)}
                  />
                </P2GBox>
              </AppCard>
            </P2GGrid>
          ))}
        </P2GGrid>
      </P2GGrid>
    </P2GGrid>
  );
};

export function QuoteCardSingleCourier(props: QuoteCardProps) {
  const [tab, setTab] = useState<ServiceTabType>('');

  const imageName = useMemo(
    () => CollectionImageName(props.quote.Service.CollectionType),
    [props.quote.Service.CollectionType]
  );

  const options = useMemo(
    () => PolyFillQuoteOptions(props.quote, props.maxQuoteOptions),
    [props.quote, props.maxQuoteOptions]
  );

  const availableExtras = useMemo(() => {
    if (!props.quote?.Options) {
      return [];
    }
    const available = props.quote.Options.flatMap((o) => o.Extras ?? []).map((e) => e.Id);
    const included = props.quote.Options.flatMap((o) => o.AvailableExtras ?? []).map((e) => e.Id);
    return [...available, ...included].filter(onlyUnique);
  }, [props.quote]);

  return (
    <>
      <P2GBox py={2}>
        <AppCard className="smartsend-quote-card smartsend-quote-card-single-courier">
          <P2GBox p={2}>
            {props.isXs ? (
              <MobileView
                availableExtras={availableExtras}
                estimatedValue={props.estimatedValue}
                imageName={imageName}
                isSelected={props.isSelected}
                onSave={props.onSave}
                options={options}
                quote={props.quote}
                setTab={setTab}
              />
            ) : (
              <DesktopView
                availableExtras={availableExtras}
                estimatedValue={props.estimatedValue}
                imageName={imageName}
                isSelected={props.isSelected}
                onSave={props.onSave}
                options={options}
                quote={props.quote}
                setTab={setTab}
              />
            )}
          </P2GBox>
        </AppCard>
      </P2GBox>
      <P2GSidePanel
        open={!!tab}
        onClose={() => setTab('')}
        anchor="right"
        title={props.quote.Service.Name}
        preTitle={
          <div style={{ width: '150px' }}>
            <P2GBox p={1} bordered>
              <P2GServiceLogo size="normal-svg" slug={props.quote.Service.Id} />
            </P2GBox>
          </div>
        }
      >
        {!!tab && (
          <Tagged tag="panel-service-information">
            <ServiceInfo
              setTab={setTab}
              tab={tab}
              itemId={null}
              key={`${props.quote.Service.Id}_${props.quote.Service.Provider}_${props.quote.Service.AccountId}`}
              id={props.quote.Service.Id}
              provider={props.quote.Service.Provider}
              accountId={props.quote.Service.AccountId}
              currencyCode={props.quote.CurrencyCode}
            />
          </Tagged>
        )}
      </P2GSidePanel>
    </>
  );
}
